import { ResourceBookingEnum } from 'store/app-instance-configs/enums';
import { AppInstanceConfig } from 'store/app-instance-configs/types';

export const EXCHANGE_INTEGRATION = 'exchange';

/** RES-1330: every integration the get-resources `integrations` param accepts, sent when Exchange v2 is on. */
export const EXCHANGE_V2_INTEGRATIONS = ['native', EXCHANGE_INTEGRATION, 'tripleseat'].join(',');

/*
 * RES-1330. Exchange integration v2: the Exchange config carries `native_resources_enabled: true` when its
 * resources have been mirrored into the native adapter. That flag, not an adapter name, is what marks a
 * config as "native-enabled Exchange" -- only Exchange configs ever carry it.
 */
export const isNativeEnabledExchangeConfig = (config?: Pick<AppInstanceConfig, 'config'> | null): boolean =>
  Boolean((config?.config as { native_resources_enabled?: boolean } | undefined)?.native_resources_enabled);

type ConfigLike = Pick<AppInstanceConfig, 'config' | 'uuid' | 'building_uuid' | 'vertical_adapter' | 'aic_owner_uuid'>;

/*
 * The Exchange config a booking must be filed under, so helix forwards it to Exchange as well as writing it
 * natively. Undefined when the booking is not an Exchange-integration resource booked through the native
 * adapter, in which case the caller keeps the active config.
 *
 * ⛔ Matched on `building_uuid`, NOT `location_id` / `alt_building_uuid` as `adapterForResource` does. On an
 * Exchange config `location_id` is the numeric owner id ("14362") and `alt_building_uuid` is an unrelated
 * uuid; only `building_uuid` equals the resource's building (verified against a real
 * app-instance-configs response). Matching the wrong field finds nothing and silently leaves the booking
 * native-only.
 */
export const findNativeEnabledExchangeConfig = (
  resource: { integration?: string; building_uuid?: string } | undefined,
  configs: Array<ConfigLike> | undefined,
  adapterName: string | undefined,
): ConfigLike | undefined => {
  if (resource?.integration !== EXCHANGE_INTEGRATION || adapterName !== ResourceBookingEnum.NativeAdapter) {
    return undefined;
  }

  return configs?.find(
    config =>
      config.vertical_adapter?.name === ResourceBookingEnum.ExchangeAdapter &&
      isNativeEnabledExchangeConfig(config) &&
      (!resource.building_uuid || config.building_uuid === resource.building_uuid),
  );
};
